// Path: libs/core/auth/src/lib/data-access/auth.service.ts
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { APP_CONFIG, AppConfig } from '@cardapio-online/config';
import {
  AuthApiResponse,
  JwtClaims,
  LoginPayload,
} from '../models/auth.interface';
import { AuthSession } from '../models/auth.types';

export type AuthState = {
  accessToken: string | null;
  isAuthenticated: boolean;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly state$ = new BehaviorSubject<AuthState>({
    accessToken: null,
    isAuthenticated: false,
  });

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly cfg: AppConfig
  ) {
    this.hydrateFromStorage();
  }

  login(payload: LoginPayload): Observable<AuthSession> {
    return this.http.post<AuthApiResponse>('/auth/login', payload, {
      headers: { 'x-tenant-slug': this.cfg.tenantSlug },
    }).pipe(
      map((response) => {
        const token = response?.data?.token ?? null;

        if (!token) {
          throw new Error('Token de autenticação ausente na resposta.');
        }

        const claims = this.parseJwtClaims(token);

        if (this.isTokenExpired(claims)) {
          this.clearSession();
          throw new Error('Token expirado.');
        }

        if (this.isTenantMismatch(claims)) {
          this.clearSession();
          throw new Error('Token inválido para o tenant atual.');
        }

        this.setToken(token);

        return {
          token,
          user: response.data.user,
          claims,
        };
      })
    );
  }

  logout(): void {
    this.clearSession();
  }

  isAuthenticated(): boolean {
    const token = this.getToken();

    if (!token) {
      return false;
    }

    const claims = this.parseJwtClaims(token);

    if (this.isTokenExpired(claims) || this.isTenantMismatch(claims)) {
      this.clearSession();
      return false;
    }

    if (!this.state$.getValue().isAuthenticated) {
      this.state$.next({
        accessToken: token,
        isAuthenticated: true,
      });
    }

    return true;
  }

  getToken(): string | null {
    const inMemory = this.state$.getValue().accessToken;
    if (inMemory) {
      return inMemory;
    }

    const persisted = localStorage.getItem(this.cfg.authStorageKey);
    return persisted && persisted.trim() ? persisted : null;
  }

  getSnapshot(): AuthState {
    return this.state$.getValue();
  }

  setToken(token: string): void {
    localStorage.setItem(this.cfg.authStorageKey, token);
    this.state$.next({
      accessToken: token,
      isAuthenticated: true,
    });
  }

  private hydrateFromStorage(): void {
    const token = localStorage.getItem(this.cfg.authStorageKey);

    if (!token || !token.trim()) {
      this.clearSession(false);
      return;
    }

    const claims = this.parseJwtClaims(token);

    if (this.isTokenExpired(claims) || this.isTenantMismatch(claims)) {
      this.clearSession();
      return;
    }

    this.state$.next({
      accessToken: token,
      isAuthenticated: true,
    });
  }

  private clearSession(removeFromStorage = true): void {
    if (removeFromStorage) {
      localStorage.removeItem(this.cfg.authStorageKey);
    }

    this.state$.next({
      accessToken: null,
      isAuthenticated: false,
    });
  }

  private parseJwtClaims(token: string): JwtClaims | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }

      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(normalized);
      return JSON.parse(decoded) as JwtClaims;
    } catch {
      return null;
    }
  }

  private isTokenExpired(claims: JwtClaims | null): boolean {
    if (!claims?.exp) {
      return false;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return claims.exp <= nowInSeconds;
  }

  private isTenantMismatch(claims: JwtClaims | null): boolean {
    if (!claims?.tslug) {
      return false;
    }

    return claims.tslug !== this.cfg.tenantSlug;
  }
}